import { z } from 'zod';

import { type QuestionType } from '../question-type/question-type';

/**
 * A calendar day, `YYYY-MM-DD`. Date answers and their bounds are days, never
 * instants: "born on 1990-03-14" is the same fact in every timezone, and
 * offset-converting it is the bug the P1 verifier already found once on
 * `users.dob`.
 */
const CalendarDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date.');

/**
 * The longest staff-authored regular expression the platform will accept.
 *
 * A pattern is compiled and run against member input on every answer write, so
 * a catastrophically backtracking expression is an availability problem, not
 * just a bad validation rule. The ceiling is not a defence against a malicious
 * staff user — anyone holding `questions.manage` has far more direct options —
 * it is a guard against the accidental monster, alongside the write-time
 * compile check in the builder service (a pattern that does not compile is a
 * 422, never a question that rejects every answer at runtime).
 */
const PatternSchema = z.string().min(1).max(200);

/** `text` / `textarea` — length bounds and an optional staff-authored pattern. */
export const TextValidationSchema = z
  .object({
    minLength: z.number().int().min(0).max(10_000).optional(),
    maxLength: z.number().int().min(1).max(10_000).optional(),
    pattern: PatternSchema.optional(),
  })
  .strict();

/** `number` — inclusive bounds. */
export const NumberValidationSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    /** Whole numbers only (ages, counts) — the difference between 3 and 3.5 children. */
    integer: z.boolean().optional(),
  })
  .strict();

/** `date` — inclusive calendar-day bounds. */
export const DateValidationSchema = z
  .object({
    minDate: CalendarDaySchema.optional(),
    maxDate: CalendarDaySchema.optional(),
  })
  .strict();

/**
 * `height` — bounds in **centimetres**, the one canonical unit (see
 * `question-type.ts`). Clients convert for display; the stored answer never
 * carries a unit, because an answer that could be either is an answer matching
 * cannot compare.
 */
export const HeightValidationSchema = z
  .object({
    minCm: z.number().int().min(50).max(300).optional(),
    maxCm: z.number().int().min(50).max(300).optional(),
  })
  .strict();

/** `multi_select` — how many options must be chosen. */
export const MultiSelectValidationSchema = z
  .object({
    minSelections: z.number().int().min(0).max(100).optional(),
    maxSelections: z.number().int().min(1).max(100).optional(),
  })
  .strict();

/**
 * The types that constrain nothing beyond their own shape.
 *
 * `.strict()` and empty rather than "anything goes": a validation blob saved
 * against a `boolean` question is a staff misunderstanding worth a 422, not a
 * silent no-op that looks configured in the builder and does nothing at runtime.
 */
export const EmptyValidationSchema = z.object({}).strict();

/**
 * The stored `validation` jsonb of a question, whatever its type. A union rather
 * than a discriminated one: the discriminator is the question's own `type`
 * **column**, and repeating it inside the blob would create a second copy that
 * could disagree with it.
 */
export type QuestionValidation =
  | z.infer<typeof TextValidationSchema>
  | z.infer<typeof NumberValidationSchema>
  | z.infer<typeof DateValidationSchema>
  | z.infer<typeof HeightValidationSchema>
  | z.infer<typeof MultiSelectValidationSchema>
  | z.infer<typeof EmptyValidationSchema>;

/**
 * The schema a question's `validation` blob must satisfy, chosen by its type.
 *
 * An **exhaustive** switch, so adding a question type is a compile error here —
 * which is the point: a type whose validation config nobody declared would
 * accept any blob and enforce none of it.
 */
export function validationSchemaFor(type: QuestionType): z.ZodType<QuestionValidation> {
  switch (type) {
    case 'text':
    case 'textarea':
      return TextValidationSchema;

    case 'number':
      return NumberValidationSchema;

    case 'date':
      return DateValidationSchema;

    case 'height':
      return HeightValidationSchema;

    case 'multi_select':
      return MultiSelectValidationSchema;

    case 'boolean':
    case 'single_select':
    case 'country':
      return EmptyValidationSchema;
  }
}

/**
 * Whether a bound pair is the right way round.
 *
 * Zod validates each field alone, so `{minLength: 10, maxLength: 5}` parses
 * cleanly and then rejects **every** answer — a question that can never be
 * satisfied, discovered by a member rather than by the staff member who saved
 * it. The builder calls this before writing.
 */
export function findInvertedBound(
  validation: QuestionValidation,
): { lower: string; upper: string } | null {
  const pairs: readonly [string, string][] = [
    ['minLength', 'maxLength'],
    ['min', 'max'],
    ['minDate', 'maxDate'],
    ['minCm', 'maxCm'],
    ['minSelections', 'maxSelections'],
  ];

  const blob: Record<string, unknown> = { ...validation };

  for (const [lower, upper] of pairs) {
    const low = blob[lower];
    const high = blob[upper];

    if (low === undefined || high === undefined) {
      continue;
    }

    // Numbers compare numerically and calendar days compare lexicographically —
    // which is exact for `YYYY-MM-DD` and the reason that format is enforced.
    const inverted =
      typeof low === 'number' && typeof high === 'number'
        ? low > high
        : typeof low === 'string' && typeof high === 'string'
          ? low > high
          : false;

    if (inverted) {
      return { lower, upper };
    }
  }

  return null;
}

/**
 * Whether a staff-authored pattern compiles. Called at **write** time so a bad
 * expression is a 422 on the builder's form rather than a 500 on a member's
 * first answer.
 */
export function isCompilablePattern(pattern: string): boolean {
  try {
    // Construction IS the check — an invalid expression throws here. The value
    // is discarded on purpose, which is why it is not assigned.
    return new RegExp(pattern, 'u') instanceof RegExp;
  } catch {
    return false;
  }
}
